import { getDay } from 'date-fns';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, forkJoin, map, of, throwError } from 'rxjs';
import {
  ServizioMensaEventoCalendarioDTO,
  ServizioMensaEventoCalendarioStatus, ServizioMensaEventoCalendarioType,
  StudenteCittadinoDettaglioService
} from '../../../core/api/generated';
import { Studente } from '../../interfaces/studente-figlio.interface';

export type ChipState = 'libero' | 'assenza' | 'pasta-in-bianco' | 'chiusura';

export interface EventiEChiusure {
  eventi: ServizioMensaEventoCalendarioDTO[];
  chiusure: ReadonlySet<string>;
}

/** `true` se l'errore indica calendario scolastico non configurato (code 1002) o assente (404). */
function isCalendarioAssente(err: unknown): boolean {
  const e = err as HttpErrorResponse;
  return e?.error?.code === 1002 || e?.status === 404;
}

/**
 * Carica in parallelo eventi (`getEventiCalendario1`) e chiusure (`getCalendarioMensa`) per uno
 * studente sul servizio mensa attivo. Nel mondo `*2` le chiusure NON sono più negli eventi.
 *
 * Solo il caso ATTESO "calendario non configurato" (404 / code 1002) degrada a "nessuna chiusura
 * nota" senza bloccare. Ogni altro guasto (rete/500) viene propagato al chiamante: una chiusura
 * non verificata NON deve passare silenziosamente per "nessuna chiusura" (rischio prenotazione su
 * giorno chiuso). Sorgente UNICA della query: palette + calendar-action-form la condividono.
 */
export function loadEventiEChiusure$(
  studentiService: StudenteCittadinoDettaglioService,
  idStudente: string,
  gestioneMenseServizioId: string
): Observable<EventiEChiusure> {
  return forkJoin({
    eventi: studentiService.listEventiCalendarioStudenteCittadino({ idStudente, gestioneMenseServizioId }),
    chiusure: studentiService.getCalendarioMensaStudenteCittadino({ idStudente, gestioneMenseServizioId }).pipe(
      map(cal => new Set((cal?.chiusure_puntuali ?? [])
        .map(d => toIsoDay(d))
        .filter((d): d is string => !!d)) as ReadonlySet<string>),
      catchError(err => isCalendarioAssente(err)
        ? of(new Set<string>() as ReadonlySet<string>)
        : throwError(() => err))
    ),
  });
}

const DAY_MAP: Record<string, number> = {
  'domenica': 0, 'lunedi': 1, 'martedi': 2, 'mercoledi': 3,
  'giovedi': 4, 'venerdi': 5, 'sabato': 6,
};

/** Marche diacritiche combinanti (U+0300–U+036F): gli accenti staccati da `normalize('NFD')`. */
const COMBINING_MARKS = /[̀-ͯ]/g;

/** Normalizza un nome-giorno per il match: minuscolo, senza accenti (es. "Lunedì" → "lunedi"). */
function normalizeDayKey(giorno: string): string {
  return giorno.toLowerCase().normalize('NFD').replace(COMBINING_MARKS, '');
}

/**
 * Giorni esclusi dal calendario (weekend di default). I giorni di chiusura custom della scuola
 * NON sono più nel contratto discovery (`Scuola` minimale, niente `giorniChiusura`): vedi change
 * `riallinea-client-api-be-baseline`. Le chiusure puntuali restano gestite da `isChiusura`
 * (`getCalendarioMensa().chiusure_puntuali`). Qui resta il solo default sab+dom.
 */
export function getExcludedDays(_studente?: Studente | null): number[] {
  return [0, 6]; // sab+dom
}

export function isExcludedDay(date: Date, excludedDays: number[]): boolean {
  return excludedDays.includes(getDay(date));
}

/**
 * Stato di una data dagli eventi mensa per-servizio (`getEventiCalendario1`). Nel mondo `*2` il
 * DTO espone solo ASSENZA/PASTA_IN_BIANCO (niente CHIUSURA_SCOLASTICA: le chiusure arrivano da
 * `getCalendarioMensa`, vedi `isChiusura`) e usa `stato` enum (si ignorano i CANCELLATO).
 */
export function getEventState(dateDDMMYYYY: string, eventi: ServizioMensaEventoCalendarioDTO[]): ChipState {
  for (const ev of eventi) {
    if (ev.data === dateDDMMYYYY && ev.stato !== ServizioMensaEventoCalendarioStatus.CANCELLATO) {
      switch (ev.tipo) {
        case ServizioMensaEventoCalendarioType.ASSENZA: return 'assenza';
        case ServizioMensaEventoCalendarioType.PASTA_IN_BIANCO: return 'pasta-in-bianco';
      }
    }
  }
  return 'libero';
}

/**
 * Normalizza una data in chiave-giorno ISO `yyyy-MM-dd`. Accetta sia ISO (eventuale `T...`) sia
 * `dd/MM/yyyy`. `null` se non riconosciuta. Usato per confrontare le date-evento (`dd/MM/yyyy`)
 * con le `chiusure_puntuali` (ISO) di `getCalendarioMensa`.
 */
export function toIsoDay(data: string | null | undefined): string | null {
  if (!data) return null;
  const slash = data.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slash) return `${slash[3]}-${slash[2]}-${slash[1]}`;
  const iso = data.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  return null;
}

/**
 * Chiusura scolastica nel giorno indicato. Le chiusure NON sono più negli eventi (mondo `*2`):
 * arrivano da `getCalendarioMensa().chiusure_puntuali` (date ISO), passate qui come Set ISO.
 */
export function isChiusura(dateDDMMYYYY: string, chiusureIso: ReadonlySet<string>): boolean {
  const iso = toIsoDay(dateDDMMYYYY);
  return iso !== null && chiusureIso.has(iso);
}

/**
 * Chiave Transloco del messaggio "servizio sospeso", o `null` se lo studente non è sospeso.
 * Funzione pura: NON traduce qui (nessun accesso a `TranslocoService`), restituisce la chiave
 * e il chiamante traduce al punto d'uso. Vedi regola i18n in CLAUDE.md.
 */
export function getSospesoKey(studente?: Studente | null): string | null {
  if (!studente?.isSospeso) return null;
  return studente.isPresenteRicaricaDaPagare
    ? 'shared.command_palette.validation.sospesoPagamenti'
    : 'shared.command_palette.validation.sospeso';
}
